// Copyright (c) StrangeBee 2023

import { classList } from '@Helpers/className';
import React, { useState, ReactElement, useEffect } from 'react';
import { THDropdown } from '@Molecules/Inputs/THDropdown/THDropdown';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THCheckbox } from '@Molecules/Inputs/THCheckbox/THCheckbox';
import { THMenu } from '@Molecules/THMenu/THMenu';

import './DisplayParams.scss';

type UseDisplayParamsReturn = [Record<string, boolean>, () => ReactElement, (s: Record<string, boolean>) => void];

interface ItemParamsProps {
    label: string;
    visible: boolean;
}

const useDisplayParams = (defaultParams: Record<string, ItemParamsProps>): UseDisplayParamsReturn => {
    const [itemsShow, setItemsShow] = useState<Record<string, ItemParamsProps>>(defaultParams);
    const [visible, setVisible] = useState<boolean>(false);
    const [itemsShowOutPut, setItemsShowOutPut] = useState<Record<string, boolean>>({});

    const handleMenuClick = (key: string) => {
        setItemsShow((state) => ({
            ...state,
            [key]: { ...state[key], visible: !state[key].visible },
        }));
    };
    const handleVisibleChange = (flag: any) => {
        setVisible(flag);
    };

    useEffect(() => {
        const treated = Object.entries(itemsShow).reduce(
            (acc, [attr, { visible }]): Record<string, boolean> => (attr ? { ...acc, [attr]: visible } : acc),
            {} as Record<string, boolean>,
        );
        setItemsShowOutPut(treated);
    }, [itemsShow]);

    return [
        itemsShowOutPut,
        () => (
            <THDropdown
                placement="bottomRight"
                dropdownClassName="display-params__wrapper"
                onVisibleChange={handleVisibleChange}
                trigger={['click']}
                triggerElem={() => (
                    <THButton type="link" className="display-params__cta" size="small">
                        &#8286;
                    </THButton>
                )}
                visible={visible}
                quitOnMenuItemClick={false}
            >
                {Object.entries(itemsShow).map(([key, { label, visible }]) =>
                    label ? (
                        <THMenu.Item
                            key={key}
                            className={classList('display-params__item', { disable: !visible })}
                            onClick={() => handleMenuClick(key)}
                            modeReverse
                        >
                            <span className="display-params__menu-label">{label}</span>
                            <THCheckbox checked={visible} />
                        </THMenu.Item>
                    ) : null,
                )}
            </THDropdown>
        ),
        (newState) => {
            Object.entries(newState).forEach(([key, visible]: [string, boolean]) => {
                setItemsShow((state) => ({
                    ...state,
                    [key]: { ...state[key], visible },
                }));
            });
        },
    ];
};

export default useDisplayParams;
